/**
 * Copyright (c) 2022 FMR LLC.
 * All Rights Reserved.
 * Fidelity Confidential Information.
 */

// Note that these are purposely in order by duration from shortest to longest
export enum Duration {
    TERM_3MO = '3-month',
    TERM_6MO = '6-month',
    TERM_9MO = '9-month',
    TERM_1YR = '1-year',
    TERM_18MO = '18-month',
    TERM_2YR = '2-year'
}

export enum ScreenerCardStyle {
    BUY_CARD,
    UNAVAILABLE_CARD
}

// This is currently a hard-coded mapping to connect a tooltip placeholder to tooltip content.
// Ideally, we should be able to create this mapping dynamically from the content itself.  That would
// require tooltip placeholders to include the corresponding content id (whic requires a content change).
// For now, we are mapping the text found in the tooltip placeholder (the "key" below) to the id statically.
export const TOOLTIP_MAPPINGS = [
    { key: 'maturity date', id: 'tcm:526-1168782' },
    { key: 'yields/rates', id: 'tcm:526-1168795' },
    { key: 'CD callable/not call-protected', id: 'tcm:526-1168794' }
];

// note: any time tridion content is updated, the tcm ids should be checked with these strings
export enum TCM_MAPPING {
    HEADER_INFO = 'tcm:526-1146462',
    SAVE = 'tcm:526-1146465',
    FCD_ICONS = 'tcm:526-1311876',
    BENEFITS_RISKS = 'tcm:526-1146467',
    FAQS = 'tcm:526-1146466',
    ADDL_RESOURCES = 'tcm:526-1146468',
    DISCLOSURES = 'tcm:526-1146469'
}

export const ClientConstants = {
    timePeriodList: Object.values(Duration).map((duration: string, key: number) => {
        return {
            key: key,
            leftValue: 'Time period',
            rightValue: duration
        };
    }),

    SLIDER_VALUES_ARRAY: [
        {
            key: 0,
            leftValue: '3-month',
            rightValue: '0.25'
        },
        {
            key: 1,
            leftValue: '6-month',
            rightValue: '0.40'
        },
        {
            key: 2,
            leftValue: '9-month',
            rightValue: '0.70'
        },
        {
            key: 3,
            leftValue: '1-year',
            rightValue: '1.00'
        },
        {
            key: 4,
            leftValue: '18-month',
            rightValue: '1.20'
        },
        {
            key: 5,
            leftValue: '2-year',
            rightValue: '1.60'
        }
    ],
    INVALID_SLIDER_GREATER_THAN_ZERO_MSG: 'invalid prop value: prop must be greater than zero',
    INVALID_SLIDER_GREATER_THAN_OR_EQUAL_ZERO_MSG: 'invalid prop value: prop must be greater than or equal to zero',
    INVALID_SLIDER_MIN_VALUE_MSG: 'invalid prop value: min must be less than max',
    INVALID_SLIDER_STEP_VALUE_MSG: 'invalid prop value: step cannot be greater than max or less than 1',
    INVALID_SLIDER_VALUE_MSG: 'invalid prop value: value cannot be greater than max or less than min',

    // urls
    OFFERINGS_URL: '/prgw/digital/finewissues/api/cdrates',
    CONTENT_URL: '/prgw/digital/finewissues/api/content',
    CONFIG_URL: 'prgw/digital/finewissues/api/config',

    // screener card
    CD_TITLE: '{{ duration }} CD',
    APY_TITLE: 'APY {{ apy }}',
    CALCULATED_RETURN: '{{ duration }} Return',
    REVIEW_AND_BUY: 'Review & Buy',
    RESEARCH_CD: 'Research this CD',
    SHORTER_TIME_PERIOD: "Can't commit to a {{ duration }} CD? Here's a shorter timeframe",
    LONGER_TIME_PERIOD: 'Open to a longer time period with a higher rate?',
    SHORTER_WHOLE_CDS: "Can't commit to a 3-month CD? Shorter, whole CDs may be available",
    LONGER_WHOLE_CDS: 'Longer timeframes available in whole CD quantities',
    FRACTIONAL_CDS_UNAVAILABLE: '{{ duration }} Fractional CDs are currently unavailable',
    ALL_CD_OFFERINGS: 'All CD Offerings',

    // error messages
    OFFERINGS_SERVICE_REQUEST_ERROR: 'Get offerings failed',
    CONTENT_SERVICE_REQUEST_ERROR: 'Get content failed',
    AMOUNT_CONTENT_INPUT_ERROR: 'Amount must be in increments of $100.',
    CONTENT_UNAVAILABLE_ERROR:
        'There was an error processing your request. Please try again or call a Fixed Income Specialist at 1-800-544-5372.',
    OFFERINGS_UNAVAILABLE_ERROR: 'We were unable to load the CD rates.  Please refresh or try again later.',
    AMOUNT_CONTENT_MAX_LIMIT_ERROR: 'Amount must be less than or equal to $9,999,900.',

    // amount modal text
    AMOUNT_CONTENT_HEADER: 'How much would you like to invest?',
    AMOUNT_CONTENT_SUB_HEADER: "Enter an amount you won't need to access for the entire time period you select.",
    AMOUNT_CONTENT_INPUT_HEADER: 'Investment amount',
    AMOUNT_CONTENT_HELPER_TEXT_1: 'Minimum investment: $100.',
    AMOUNT_CONTENT_HELPER_TEXT_2: 'Enter in increments of $100.',

    // duration modal text
    DURATION_CONTENT_HEADER: 'How long before you need to access this money?',
    DURATION_CONTENT_SUB_HEADER:
        'Remember, early withdrawals can result in your losing a portion of your original investment.',
    DURATION_CONTENT_OPTIONS_BUTTON: 'View your options',

    // main page text
    GET_THIS_RATE_BUTTON_TEXT: 'Get this rate',
    TIMESTAMP_MESSAGE: 'As of {{ timestamp }}. Higher yields may be available on whole CDs ($1000 increment).',
    GS_TIMESTAMP_MESSAGE: 'All CD data as of {{ timestamp }}.',
    APY_TOOLTIP_HEADING: 'Annual Percentage Yield (APY)',
    APY_TOOLTIP_TEXT:
        'The amount an investor makes on an investment, expressed as an annualized rate of return. Therefore, for CDs maturing in under a year, the total return you receive in cash terms is proportionate to the length of the investments period. For example, if you invest $1,000 into a 1% APY CD maturing in 12 months, the interest you would earn would be $10. If you put the same $1,000 into a 1% APY CD maturing in 6 months, you would receive $5 in interest.',

    // guided screener
    SCREENER_TITLE: 'Guided search results',
    SCREENER_INPUT_TITLE: 'Investment amount',

    SCREENER_NO_CDS: "Sorry, we couldn't find any results.",
    SCREENER_ONE_OPTION: 'With a {{ amount }} investment and a {{ duration }} maturity the highest rate available is:',
    SCREENER_TWO_OPTIONS: 'With a {{ amount }} investment and a {{ duration }} maturity you have two options:',

    SCREENER_OPTION_FRACTIONAL: 'Buy a single fractional CD',
    SCREENER_OPTION_WHOLE: 'Buy a single whole CD',
    SCREENER_OPTION1: 'Option 1: Buy a single fractional CD',
    SCREENER_OPTION2: 'Option 2: Earn more by buying a whole CD and a fractional CD',

    SCREENER_CARD_NO_CDS: '{{ duration }} CDs are currently unavailable',
    SCREENER_CARD_NO_FRACTIONAL_CDS: '{{ duration }} Fractional CDs are currently unavailable',
    SCREENER_CARD_FRACTIONAL_AMOUNT: 'Fractional CD for {{ amount }}',
    SCREENER_CARD_WHOLE_AMOUNT: 'Whole CD for {{ amount }}',

    SCREENER_CONSIDERATIONS_TITLE: 'Considerations',
    SCREENER_CONSIDERATIONS_RETURN: 'Total Return of {{ return }}',
    SCREENER_CONSIDERATIONS_ONE_TRANSACTION: 'One simple transaction',
    SCREENER_CONSIDERATIONS_ONE_DATE: 'One maturity date {{ date }}',
    SCREENER_CONSIDERATIONS_COMBINED_RETURN: 'Combined Total Return of {{ return }}<sup>*</sup>',
    SCREENER_CONSIDERATIONS_EARN_MORE: 'Earn {{ advantage }} more<sup>*</sup>',
    SCREENER_CONSIDERATIONS_TWO_TRANSACTIONS: 'Two transactions',
    SCREENER_CONSIDERATIONS_BOTH_MATURITY_DATE: 'Both CDs mature on {{ date }}',
    SCREENER_CONSIDERATIONS_FRACTIONAL_MATURITY_DATE: 'Fractional CD maturity date {{ date }}',
    SCREENER_CONSIDERATIONS_WHOLE_MATURITY_DATE: 'Whole CD maturity date {{ date }}',
    SCREENER_CONSIDERATIONS_MAY_BE_CALLABLE: 'Whole CD may be callable<sup>*</sup>',

    // Passing a class to v-html (below) doesn't appear to work so using style property instead
    // const replacement = '<span class="currency">' + this.formatDollarAmount(this.topCard.returnAmount) + '</span>';
    SCREENER_GREEN_SPAN_OPEN: '<span style="color: #0D6F3F; margin-left: 0 !important;">',
    SCREENER_GREEN_SPAN_CLOSE: '</span>',

    SCREENER_SEARCH_TIPS_TITLE: 'Search tips',
    SCREENER_SEARCH_TIPS_INCREASE: 'Increase investment by {{ amount }}',
    SCREENER_SEARCH_TIPS_DECREASE: 'Decrease investment by {{ amount }}',
    SCREENER_SEARCH_TIPS_DURATION: 'Change time period',
    SCREENER_SEARCH_TIPS_FRACTIONAL_TO_WHOLE:
        'If you increase your investment by {{ amount }}, you can earn a higher rate of {{ apy }}',

    SCREENER_OFFERINGS_LINK: 'See our full offering, including all maturities',
    SCREENER_SELECT_LINK: 'How did we select these CDs?',
    SCREENER_SELECT: {
        INFO_1: 'Our guided search experience uses a few simple criteria to show you some options that may be of interest to you. Based on the Investment amount and the Time period you select, we show the highest rate new issue CD available.',
        INFO_2: 'We determine available CDs based on the amount of your initial investment. We use the investment minimums & increments of the CD, $100 for fractional CDs and $1000 for whole CDs, as well as the available quantity of an offering to display CDs that align with your investment amount.',
        INFO_3: 'Please note that if you enter a fractional amount, this tool will select only fractional CDs which will exclude callable and step-up CDs. However, if you enter a whole CD amount (multiples of $1000) this tool will display the entire universe of new issue CDs including Fractional, Callable, and Step-up CDs.'
    },
    SCREENER_DISCLOSURE_LINK: 'View other important information and risks of investing in fixed income securities',
    SCREENER_DISCLOSURE_ASTERICK:
        '* The amount of interest generated assuming the CD is not called prior to and held until the date of maturity.',
    FDIC_LINK: 'Important Information About FDIC Insurance Coverage',
    FDIC_INFO:
        'For purposes of FDIC insurance coverage limits, all depository assets of the account holder at the institution that issued the CD' +
        ' will generally be counted toward the aggregate limit (usually $250,000) for each applicable category of account. Investors should' +
        ' consider the extent to which other accounts, deposits or accrued interest held at Fidelity or another institution,' +
        ' may, when aggregated, exceed applicable FDIC limits. FDIC insurance does not cover market losses. All the new issue brokered CDs' +
        ' Fidelity offers are FDIC insured. For additional details on FDIC insurance limits, see',
    FDIC_URL: 'www.fdic.gov',

    // buttons
    CONTACT_US: 'Contact us',

    // generic text
    NEXT_BUTTON: 'Next',
    BACK_BUTTON: 'Back',

    // urls & queries
    ALL_CDS_QUERY: '?name=CD&refpr=obrfind15&requestpage=FISearchCD',
    CONTACT_US_URL: 'https://www.fidelity.com/customer-service/contact-us',
    OPEN_ACCOUNT_URL: 'https://www.fidelity.com/open-account/overview',

    // screen max widths
    MOBILE_MAX: 512,
    TABLET_MAX: 768,
    DESKTOP_MAX: 1280,

    // breadcrumbs
    HOME: 'Home',
    INVESTMENT_PRODUCTS: 'Investment Products',
    FI_BONDS: 'Fixed Income & Bonds',
    RESEARCH: 'Research',
    FRACTIONAL_CDS: 'Fractional CDs',
    BREADCRUMB_ARROWS: '»',
    PRINT: 'Print',
    EMAIL: 'Email',
    SHARE: 'Share',

    // adobe tracking
    TAG_TEMPLATE: {
        pt: 'ss',
        action: 'pageview',
        channel: 'Fid.com web',
        sec: 'fixed income',
        sec1: 'cd',
        pname: 'page name'
    },
    EDU_PAGE: 'New Issue Guided Experience',
    SEARCH_PAGE: 'New Issue Guided Experience Results',
    TOP_CARD_INTERACTION: 'option 1 - single cd',
    WHOLE_CARD_INTERACTION: 'option 2 - mixed lot, whole cd',
    FRACTIONAL_CARD_INTERACTION: 'option 2 - mixed lot, fractional cd'
};
